/**
 * CategoryView Component
 *
 * Displays all questions for a single category
 * Includes:
 * - Category header with description
 * - List of questions
 * - Category progress indicator
 * - Navigation buttons
 */

import React from "react";

import type {
  QuestionnaireCategory,
  QuestionnaireQuestion,
  AssessmentResponse,
} from "../questionnaire.service";
import { getQuestionsForCategory } from "../utils/navigation.utils";

import { QuestionCard } from "./QuestionCard";

interface CategoryViewProps {
  category: QuestionnaireCategory;
  questions: QuestionnaireQuestion[];
  responses: Map<string, AssessmentResponse>;
  onResponseChange: (
    questionId: string,
    value: number,
    notes?: string,
    saveImmediately?: boolean,
  ) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirst: boolean;
  isLast: boolean;
  categoryNumber: number;
  totalCategories: number;
}

export const CategoryView: React.FC<CategoryViewProps> = ({
  category,
  questions,
  responses,
  onResponseChange,
  onNext,
  onPrevious,
  isFirst,
  isLast,
  categoryNumber,
  totalCategories,
}) => {
  const categoryQuestions = getQuestionsForCategory(category.id, questions);

  // Calculate category completion
  const answeredInCategory = categoryQuestions.filter((q) =>
    responses.has(q.id),
  ).length;
  const categoryProgress = Math.round(
    (answeredInCategory / categoryQuestions.length) * 100,
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Category Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-4 sm:p-6 mb-6 shadow-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs sm:text-sm font-medium opacity-90 mb-1">
              Category {categoryNumber} of {totalCategories}
            </div>
            <h2 className="text-lg sm:text-2xl font-bold">{category.name}</h2>
          </div>

          {/* Category Progress Badge */}
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-2 py-1 sm:px-4 sm:py-2 ml-3 flex-shrink-0">
            <div className="text-[10px] sm:text-xs font-medium opacity-90 mb-1">
              Progress
            </div>
            <div className="text-lg sm:text-2xl font-bold">
              {answeredInCategory}/{categoryQuestions.length}
            </div>
          </div>
        </div>

        <p className="text-blue-50 text-sm sm:text-base">
          {category.description}
        </p>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-medium">Category Completion</span>
            <span className="text-xs font-medium">{categoryProgress}%</span>
          </div>
          <div className="w-full bg-blue-900/30 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${categoryProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-6 mb-8">
        {categoryQuestions.map((question) => {
          const response = responses.get(question.id);

          return (
            <QuestionCard
              key={question.id}
              question={question}
              value={response?.response_value}
              contextNotes={response?.context_text}
              onChange={(value, notes, saveImmediately) =>
                onResponseChange(question.id, value, notes, saveImmediately)
              }
              required={question.is_required}
            />
          );
        })}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center sticky bottom-0 bg-white border-t border-gray-200 p-3 sm:p-4 shadow-lg rounded-t-lg gap-2">
        <button
          onClick={onPrevious}
          disabled={isFirst}
          className={`flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-6 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-all ${
            isFirst
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          <svg
            className="w-4 h-4 sm:w-5 sm:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="hidden sm:inline">Previous</span>
        </button>

        <div className="text-xs sm:text-sm text-gray-600 text-center flex-shrink min-w-0">
          {answeredInCategory === categoryQuestions.length ? (
            <span className="flex items-center gap-1 sm:gap-2 text-green-600 font-medium">
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="hidden sm:inline">Category </span>Complete
            </span>
          ) : (
            <span>
              {answeredInCategory}/{categoryQuestions.length}
            </span>
          )}
        </div>

        <button
          onClick={onNext}
          disabled={isLast && answeredInCategory < categoryQuestions.length}
          className={`flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-6 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-all ${
            isLast
              ? answeredInCategory === categoryQuestions.length
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {isLast ? (
            "Submit"
          ) : (
            <>
              <span className="hidden sm:inline">Next Category</span>
              <span className="sm:hidden">Next</span>
            </>
          )}
          <svg
            className="w-4 h-4 sm:w-5 sm:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
